import React from "react"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api"
import { useAlert, useForm, useOperator, useUser } from "hooks"

import { FormElement, formStatusEnum } from "components/Form"
import { Button, CopyToClipboard, Spinner } from "components/Elements"
import { ErrorResponse } from "models"
import { NeedHelpBlock } from "../NeedHelpBlock"
import {
	BikeRegistrationActionType,
	useBikeRegistrationProcess,
} from "features/dashboard/hooks/useBikeRegistrationProcess"
import { finalizeBikeRegistration } from "./CodeValidationProcess"
import { BICYCODE_ASSO_EMAIL } from "operators/bicycode/config"
import { sanitizeCode } from "utils"
import "./CodeValidationForm.scoped.scss"

type CodeValidationFormProps = {
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>
	refreshBikes: () => Promise<void>
}

type defaultFormFieldsProps = {
	code: string
}

const defaultFormFields: defaultFormFieldsProps = {
	code: "",
}

const CODE_PASSPORT_MIN_LENGTH = 5 // CODE PASSPORT => BETWEEN 5 AND 6 CHARACTERS
const CODE_ID_LENGTH = 6

export const ACTIVATION_CODE_TYPE = "ACTIVATION_CODE"
const PASSPORT_CODE_TYPE = "PASSPORT_CODE"

export const CodeValidationForm: React.FC<CodeValidationFormProps> = ({
	setModalIsOpen,
	refreshBikes,
}) => {
	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: defaultFormFields,
	})

	const { bikeRegistrationDispatch, bikeRegistrationState } = useBikeRegistrationProcess()
	const [codeType, setCodeType] = React.useState<string>(ACTIVATION_CODE_TYPE)
	const [showCodeInfo, setShowCodeInfo] = React.useState<boolean>(false)

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { user } = useUser()
	const { isBicycode } = useOperator()

	const bikeOwnerCensorMail = bikeRegistrationState?.bikeInfo?.censor_mail || ""

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const resendCode = async () => {
		try {
			setFormStatus(formStatusEnum.loading)
			const { getError } = await BikeApi.getBikePassword(bikeRegistrationState?.bikeId)
			if (getError()) throw getError()
			alert("success", t("code_resend", { ns: "form" }))
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		} finally {
			setFormStatus(formStatusEnum.ready)
			setFormFields((prevFormFields: defaultFormFieldsProps) => ({
				...prevFormFields,
				code: "",
			}))
		}
	}

	const resetToInitialStep = () => {
		bikeRegistrationDispatch({
			type: BikeRegistrationActionType.INITIAL_STEP,
		})
		setModalIsOpen(false)
	}

	const resetCode = () =>
		setFormFields((prevFormFields: defaultFormFieldsProps) => ({
			...prevFormFields,
			code: "",
		}))

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				await finalizeBikeRegistration({
					code: sanitizeCode(formFields.code),
					codeType,
					user,
					bike: bikeRegistrationState,
					bikeDispatch: bikeRegistrationDispatch,
					refreshBikes,
					setModalIsOpen,
				})
			} catch (error) {
				console.log(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<form className="mx-auto">
			<div className="px-6">
				<div className="text-xs text-center mb-6">
					<p className="mb-4">
						{bikeOwnerCensorMail && (
							<>
								{t("add_modal.received_code", {
									ns: "bike",
								})}
								<span className="font-bold">{bikeOwnerCensorMail}</span>.
							</>
						)}
						{!bikeOwnerCensorMail &&
							isBicycode &&
							t("add_modal.code_already_send", {
								ns: "bike",
							})}
					</p>
					{isBicycode && (
						// Ce texte est affiché uniquement pour les clients Bicycode
						<p>
							{t("add_modal.passport_code_text", {
								ns: "bike",
							})}
						</p>
					)}
				</div>

				<p className="text-xs mb-6 mx-10">
					{t("add_modal.bike", { ns: "bike" })}{" "}
					<span className="font-bold">{bikeRegistrationState?.bikeId}</span>
				</p>

				<div className="grid mx-10">
					<div className="flex justify-between">
						<button
							className={`c-tab-button ${
								codeType === ACTIVATION_CODE_TYPE ? "is-active" : ""
							}`}
							onClick={() => {
								resetCode()
								setCodeType(ACTIVATION_CODE_TYPE)
							}}
							type="button"
						>
							{t("code", {
								ns: "user",
							})}
						</button>

						{isBicycode && (
							<button
								className={`c-tab-button ${
									codeType === PASSPORT_CODE_TYPE ? "is-active" : ""
								}`}
								onClick={() => {
									resetCode()
									setCodeType(PASSPORT_CODE_TYPE)
								}}
								type="button"
							>
								{t("add_bike_page.passport_code", {
									ns: "bike",
								})}
							</button>
						)}
					</div>

					{codeType === ACTIVATION_CODE_TYPE && (
						<>
							<FormElement
								{...formElementCommonProps}
								value={formFields.code}
								name="code"
								type="code"
								id="code"
								className="mb-1 c-input-code-verification--s"
								required={true}
								codeLength={CODE_ID_LENGTH}
							/>

							<button
								type="button"
								onClick={resendCode}
								className="underline text-xxs mb-2 text-primary justify-self-end"
							>
								{t("resend_code", {
									ns: "user",
								})}
							</button>
						</>
					)}

					{codeType === PASSPORT_CODE_TYPE && (
						<>
							<FormElement
								{...formElementCommonProps}
								value={sanitizeCode(formFields.code)}
								name="code"
								type="text"
								id="code"
								className="mb-1"
								required={true}
								maxLength={CODE_ID_LENGTH}
							/>
							<button
								type="button"
								onClick={() =>
									setShowCodeInfo((prevShowCodeInfo) => !prevShowCodeInfo)
								}
								className="underline text-xxs mb-2 text-primary justify-self-end"
							>
								{t("add_bike_page.passport_code_lost", {
									ns: "bike",
								})}
							</button>

							{showCodeInfo && (
								<p className="text-xxs text-center">
									{t("add_bike_page.passport_support_contact", {
										ns: "bike",
									})}
									{t("add_bike_page.support_contact", {
										ns: "bike",
									})}
									<br />
									{isBicycode && (
										<CopyToClipboard
											text={BICYCODE_ASSO_EMAIL}
											className="font-bold pl-1 text-primary"
										/>
									)}
								</p>
							)}
						</>
					)}
				</div>

				{isReady && (
					<div className="flex justify-center pt-6">
						<Button
							onClick={handleSubmit}
							className="mx-2 px-16 py-1 min-w-min"
							variant="secondary"
							disabled={
								codeType === ACTIVATION_CODE_TYPE
									? formFields.code.length < CODE_ID_LENGTH
									: formFields.code.length < CODE_PASSPORT_MIN_LENGTH
							}
						>
							{t("global.confirm")}
						</Button>
						<Button
							onClick={resetToInitialStep}
							className="mx-2 px-6 py-1 min-w-min"
							variant="secondaryBordered"
						>
							{t("global.cancel")}
						</Button>
					</div>
				)}

				{isLoading && (
					<div className="flex justify-center my-8">
						<Spinner color={"var(--primaryColor)"} size="100px" />
					</div>
				)}
			</div>

			{isBicycode && <NeedHelpBlock />}
		</form>
	)
}
